import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// API helpers live here (not services/) — page-specific calls colocated with
// the page module. One-call aggregate powering all three tabs.
export const getProductDetail = async (brandId, sku) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/product-detail/${encodeURIComponent(sku)}`);
  return response.data;
};
// rows: [{ geography, rrp, currency, is_active }] — is_active 0 removes.
export const saveProductRrp = async (brandId, sku, rows) => {
  const response = await api.put(`${API_BASE}/api/cogs/brands/${brandId}/product-rrp/${encodeURIComponent(sku)}`, { rows });
  return response.data;
};
// Bulk "stop using this component from endDate" across ALL the brand's BOMs
// (history preserved — lines are end-dated, not deleted).
export const endDateComponentBomLines = async (brandId, sku, endDate) => {
  const response = await api.post(
    `${API_BASE}/api/cogs/brands/${brandId}/components/${encodeURIComponent(sku)}/end-date-bom-lines`,
    endDate ? { end_date: endDate } : {}
  );
  return response.data;
};

/**
 * Product page tabs: Costs / Pricing / BOM (Rishabh 2026-07-02 Product-module
 * batch). All three consume the one-call aggregate from
 * GET /api/cogs/brands/:brand_id/product-detail/:sku — see
 * backend/cogs-management/product-detail-controller.js for the payload shape.
 */

const GEO_META = {
  US: { flag: '🇺🇸', label: 'United States' },
  CA: { flag: '🇨🇦', label: 'Canada' },
  UK: { flag: '🇬🇧', label: 'United Kingdom' },
  EU: { flag: '🇪🇺', label: 'Europe' },
  AU: { flag: '🇦🇺', label: 'Australia' },
  NZ: { flag: '🇳🇿', label: 'New Zealand' },
  SG: { flag: '🇸🇬', label: 'Singapore' },
  MY: { flag: '🇲🇾', label: 'Malaysia' },
  ASIA: { flag: '🌏', label: 'Asia (suppliers)' },
  INT: { flag: '🌐', label: 'International' },
  ALL: { flag: '🌍', label: 'All geographies' },
  OTHER: { flag: '🌐', label: 'Other' }
};
const GEO_ORDER = ['US', 'CA', 'UK', 'EU', 'AU', 'NZ', 'SG', 'MY', 'ASIA', 'INT', 'ALL', 'OTHER'];

// Sensible default currency per geography for new RRP rows (editable).
const GEO_DEFAULT_CURRENCY = {
  US: 'USD', CA: 'CAD', UK: 'GBP', EU: 'EUR', AU: 'AUD', NZ: 'NZD',
  SG: 'SGD', MY: 'MYR', INT: 'USD'
};
const RRP_GEO_CHOICES = ['US', 'CA', 'UK', 'EU', 'AU', 'NZ', 'SG', 'MY', 'INT'];
const RRP_CURRENCY_CHOICES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'NZD', 'SGD', 'MYR', 'CHF', 'THB'];

function geoFlag(geo) {
  return (GEO_META[geo] || GEO_META.OTHER).flag;
}
function geoLabel(geo) {
  return (GEO_META[geo] || { label: geo }).label;
}
function geoSort(a, b) {
  const ia = GEO_ORDER.indexOf(a); const ib = GEO_ORDER.indexOf(b);
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || String(a).localeCompare(String(b));
}

function formatMoney(value, currency) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(Number(value));
  } catch (e) {
    return `${currency || ''} ${Number(value).toFixed(2)}`;
  }
}

// "US$4.26" or "US$4.26 – US$7.80" for warehouse-varying costs
function formatMoneyRange(min, max, currency) {
  if (min === null || min === undefined) return '—';
  if (max === null || max === undefined || Number(max) === Number(min)) return formatMoney(min, currency);
  return `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`;
}

// Small tag for which cost tier the resolved component cost came from, so a real
// receipt cost is visibly distinct from a stale migrated estimate.
function costSourceTag(source) {
  switch (source) {
    case 'goods_receipt': return { label: 'Actual (purchase)', color: 'success.main', title: 'Actual cost from a purchase-order receipt' };
    case 'goods_receipt_landed_2026wave': return { label: 'Actual (landed)', color: 'success.main', title: 'Landed unit cost from the goods receipt’s shipment, FX-converted to USD (2026-08 cost restatement)' };
    case 'warehouse_transfer_receive': return { label: 'Actual (transfer)', color: 'success.main', title: 'Actual cost from a warehouse-transfer receipt' };
    case 'niall_shz_ledger': return { label: 'Supplier ledger', color: 'info.main', title: 'Factory cost from the supplier ledger (RMB→USD converted)' };
    case 'sandandsky-sheet-2026': return { label: 'Cost sheet (landed)', color: 'info.main', title: 'Sand & Sky master cost sheet 2026 — landed cost, payment-weighted FX' };
    case 'sandandsky-cost-sheet-2026': return { label: 'Cost sheet (landed)', color: 'info.main', title: 'SS EF cost sheet — landed cost (base + freight + customs)' };
    case 'cin7-spf-sheet': return { label: 'Cost sheet (SPF)', color: 'info.main', title: 'SPF cost sheet (“Final Product” tab) — manually maintained post-acquisition Sand & Sky COGS' };
    case 'manual': return { label: 'Manually set', color: 'info.main', title: 'Manually entered / overridden cost' };
    case 'migration_fixed': return { label: 'Estimated (migration)', color: 'text.disabled', title: 'Estimated cost migrated at acquisition — 2020-01-01 is a system stamp, not a business date' };
    case 'manual_kit_family_copy': return { label: 'Copied (kit family)', color: 'info.main', title: 'Copied from the kit family’s cost row (2026-07-08 COGS cleanup)' };
    case 'manual_alias_copy': return { label: 'Copied (alias)', color: 'info.main', title: 'Copied from the alias SKU’s cost row (2026-07-08 COGS alias fix)' };
    default: return null;
  }
}

// A component MANUFACTURED on-site incurs no inbound freight, so its cost-sheet /
// receipt value is the EX-FACTORY manufacture cost — not a landed cost (landing only
// happens once the finished good is shipped out to a warehouse, per Rishabh 2026-08-31).
// For such SKUs (identified by the "MANUFACTURE" naming convention) relabel
// "(landed)" -> "(ex-factory)"; purchased components that genuinely ship in keep "(landed)".
function isManufactureSku(name) {
  return /manufacture/i.test(String(name || ''));
}
// Pull the source reference (transfer / goods-receipt / PO number) out of a cost
// row's notes so an "Actual (transfer)" / "Actual (purchase)" row shows WHICH
// document it came from. The number lives in product_cogs.notes, e.g.
// 'From transfer TRF-779' or 'From GR GR-20260910-2637 (PO EF-78111)'. (Rishabh
// 2026-09-15: an Actual (transfer) row must show its transfer number.)
function costSourceRef(notes) {
  const m = String(notes || '').match(/\b(?:TRF|XFER|GR|PO)[-\w]+/);
  return m ? m[0] : '';
}

function resolveCostTag(source, name) {
  const tag = costSourceTag(source);
  if (tag && isManufactureSku(name) && tag.label.includes('(landed)')) {
    return {
      ...tag,
      label: tag.label.replace('(landed)', '(ex-factory)'),
      title: `${tag.title} — manufactured on-site, so this is the ex-factory manufacture cost (no inbound freight); it becomes a landed cost only once shipped to a warehouse.`,
    };
  }
  return tag;
}

// These sources stamp effective_from = 2020-01-01 as an RMT version key, not a
// business date (verified fleet-wide: all 6,960 active 2020-01-01 rows belong to
// exactly these three sources; every other source has zero such rows).
const PSEUDO_DATE_SOURCES = new Set(['migration_fixed', 'manual_kit_family_copy', 'manual_alias_copy']);
const COST_PSEUDO_DATE = '2020-01-01';
function isPseudoDate(row) {
  return PSEUDO_DATE_SOURCES.has(row.data_source) && String(row.effective_from || '').slice(0, 10) === COST_PSEUDO_DATE;
}

function EmptyState({ title, hint }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
      <Typography variant="subtitle1" fontWeight={500} gutterBottom>{title}</Typography>
      {hint && <Typography variant="body2">{hint}</Typography>}
    </Box>
  );
}

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

/* ============================== COSTS TAB ============================== */

export function ProductCostsTab({ detail, loading }) {
  const costs = useMemo(() => detail?.costs || [], [detail]);

  const grouped = useMemo(() => {
    const byGeo = new Map();
    for (const row of costs) {
      const geo = row.geography || 'OTHER';
      if (!byGeo.has(geo)) byGeo.set(geo, []);
      byGeo.get(geo).push(row);
    }
    return Array.from(byGeo.entries()).sort((a, b) => geoSort(a[0], b[0]));
  }, [costs]);

  const summary = useMemo(() => {
    const priced = costs.filter((c) => Number(c.cogs) > 0);
    if (!priced.length) return null;
    // Costs can in principle carry mixed currencies — summarize per currency.
    const byCur = new Map();
    for (const c of priced) {
      const cur = c.currency || 'USD';
      if (!byCur.has(cur)) byCur.set(cur, { min: c, max: c });
      else {
        const s = byCur.get(cur);
        if (Number(c.cogs) < Number(s.min.cogs)) s.min = c;
        if (Number(c.cogs) > Number(s.max.cogs)) s.max = c;
      }
    }
    return { byCur: Array.from(byCur.entries()), count: priced.length };
  }, [costs]);

  // Trace for the inactive-warehouse filter — hidden rows must be visible as
  // a count, not silently vanish (QA 2026-07-03).
  const hiddenInactive = Number(detail?.costs_hidden_inactive) || 0;

  if (loading) return <LoadingState />;
  if (!costs.length) {
    return (
      <EmptyState
        title="No costs recorded for this product yet"
        hint={hiddenInactive > 0
          ? `${hiddenInactive} cost${hiddenInactive > 1 ? 's' : ''} at inactive warehouses ${hiddenInactive > 1 ? 'are' : 'is'} hidden. Reactivate the warehouse to see them.`
          : 'Add per-warehouse costs from the Details tab (Warehouse-Specific COGS).'}
      />
    );
  }

  return (
    <Box>
      {hiddenInactive > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hiddenInactive} cost row{hiddenInactive > 1 ? 's' : ''} at inactive warehouses hidden
        </Typography>
      )}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {summary.byCur.map(([cur, s]) => (
            <React.Fragment key={cur}>
              <Card variant="outlined" sx={{ minWidth: 190 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Lowest cost ({cur})</Typography>
                  <Typography variant="h6" fontWeight={600}>{formatMoney(s.min.cogs, cur)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {geoFlag(s.min.geography)} {s.min.warehouse_name || s.min.warehouse_code}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ minWidth: 190 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Highest cost ({cur})</Typography>
                  <Typography variant="h6" fontWeight={600}>{formatMoney(s.max.cogs, cur)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {geoFlag(s.max.geography)} {s.max.warehouse_name || s.max.warehouse_code}
                  </Typography>
                </CardContent>
              </Card>
            </React.Fragment>
          ))}
          <Card variant="outlined" sx={{ minWidth: 150 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Warehouses priced</Typography>
              <Typography variant="h6" fontWeight={600}>{summary.count}</Typography>
              <Typography variant="caption" color="text.secondary">across {grouped.length} geographies</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Warehouse</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Unit cost</TableCell>
              <TableCell align="right">Shipping</TableCell>
              <TableCell align="right">Inbound freight</TableCell>
              <TableCell>Effective from</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grouped.map(([geo, rows]) => (
              <React.Fragment key={geo}>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={7} sx={{ py: 0.75 }}>
                    <Typography variant="subtitle2" component="span">
                      {geoFlag(geo)} {geoLabel(geo)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                      {rows.length} warehouse{rows.length > 1 ? 's' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
                {rows.map((row) => (
                  <TableRow key={`${geo}-${row.warehouse_code}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{row.warehouse_name || row.warehouse_code}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{row.warehouse_code}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.warehouse_type ? (
                        <Chip size="small" variant="outlined" label={row.warehouse_type} />
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>{formatMoney(row.cogs, row.currency)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.warehouse_shipping_cost) > 0 ? formatMoney(row.warehouse_shipping_cost, row.currency) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.inbound_freight_cost) > 0 ? formatMoney(row.inbound_freight_cost, row.currency) : '—'}
                    </TableCell>
                    <TableCell>
                      {isPseudoDate(row) ? (
                        <Typography variant="caption" color="text.disabled" title="System stamp (2020-01-01), not a business date" sx={{ cursor: 'help' }}>—*</Typography>
                      ) : (
                        <Typography variant="caption">{String(row.effective_from || '').slice(0, 10)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {resolveCostTag(row.data_source, detail?.product?.product_name) ? (
                        <Typography variant="caption" title={resolveCostTag(row.data_source, detail?.product?.product_name).title}
                          sx={{ color: resolveCostTag(row.data_source, detail?.product?.product_name).color, cursor: 'help' }}>
                          {resolveCostTag(row.data_source, detail?.product?.product_name).label}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">{row.data_source || ''}</Typography>
                      )}
                      {costSourceRef(row.notes) && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {costSourceRef(row.notes)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}

/* ============================= PRICING TAB ============================= */

export function ProductPricingTab({ detail, loading, onSaveRrp, savingRrp }) {
  const product = detail?.product;
  const tiers = detail?.tiers || [];
  const shopifyPrices = detail?.shopify_prices || [];
  const [rows, setRows] = useState(null); // null = not yet seeded from detail
  const [removed, setRemoved] = useState([]);

  // Seed the editable grid once per loaded detail payload.
  const seededRows = useMemo(() => (detail?.rrp || []).map((r) => ({
    geography: r.geography,
    rrp: String(r.rrp),
    currency: r.currency
  })), [detail]);
  const effectiveRows = rows === null ? seededRows : rows;

  const usedGeos = new Set(effectiveRows.map((r) => r.geography));
  const addableGeos = RRP_GEO_CHOICES.filter((g) => !usedGeos.has(g));

  const dirty = rows !== null && (
    removed.length > 0 ||
    JSON.stringify(rows) !== JSON.stringify(seededRows)
  );

  const addGeo = (geo) => {
    setRows([...(effectiveRows), { geography: geo, rrp: '', currency: GEO_DEFAULT_CURRENCY[geo] || 'USD' }]);
    setRemoved(removed.filter((g) => g !== geo));
  };
  // One-click adopt of a live Shopify price as the geography's RRP.
  const adoptShopifyPrice = (geo, value, currency) => {
    const idx = effectiveRows.findIndex((r) => r.geography === geo);
    const next = idx >= 0
      ? effectiveRows.map((r, i) => (i === idx ? { ...r, rrp: String(value), currency: currency || r.currency } : r))
      : [...effectiveRows, { geography: geo, rrp: String(value), currency: currency || GEO_DEFAULT_CURRENCY[geo] || 'USD' }];
    setRows(next);
    setRemoved(removed.filter((g) => g !== geo));
  };
  const updateRow = (idx, patch) => {
    const next = effectiveRows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setRows(next);
  };
  const removeRow = (idx) => {
    const geo = effectiveRows[idx].geography;
    setRows(effectiveRows.filter((_, i) => i !== idx));
    // Only geographies that existed server-side need an is_active=0 tombstone.
    if ((detail?.rrp || []).some((r) => r.geography === geo)) setRemoved([...removed, geo]);
  };
  const handleSave = () => {
    const payload = [
      ...effectiveRows
        .filter((r) => r.rrp !== '' && !Number.isNaN(Number(r.rrp)))
        .map((r) => ({ geography: r.geography, rrp: Number(r.rrp), currency: r.currency, is_active: 1 })),
      ...removed.map((geo) => {
        const prev = (detail?.rrp || []).find((r) => r.geography === geo);
        return { geography: geo, rrp: prev ? Number(prev.rrp) : 0, currency: prev ? prev.currency : 'USD', is_active: 0 };
      })
    ];
    onSaveRrp(payload, () => { setRows(null); setRemoved([]); });
  };

  if (loading) return <LoadingState />;

  const tiersByGeo = new Map();
  for (const t of tiers) {
    const geo = t.geography || 'OTHER';
    if (!tiersByGeo.has(geo)) tiersByGeo.set(geo, []);
    tiersByGeo.get(geo).push(t);
  }
  const tierGroups = Array.from(tiersByGeo.entries()).sort((a, b) => geoSort(a[0], b[0]));

  return (
    <Box>
      {/* --- RRP by geography --- */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>RRP by geography</Typography>
              <Typography variant="caption" color="text.secondary">
                Recommended retail price per market{product && Number(product.msrp) > 0
                  ? ` · legacy single MSRP on file: ${formatMoney(product.msrp, product.msrp_currency || 'USD')}`
                  : ''}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              startIcon={savingRrp ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || savingRrp}
            >
              {savingRrp ? 'Saving…' : 'Save RRP'}
            </Button>
          </Box>

          {effectiveRows.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No RRP set yet for any geography — add the markets this product retails in below.
            </Alert>
          )}

          {effectiveRows.map((row, idx) => (
            <Box key={row.geography} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
              <Chip label={`${geoFlag(row.geography)} ${row.geography}`} sx={{ minWidth: 90, fontWeight: 600 }} />
              <TextField
                size="small"
                label="RRP"
                type="number"
                value={row.rrp}
                onChange={(e) => updateRow(idx, { rrp: e.target.value })}
                inputProps={{ min: 0, step: '0.01' }}
                sx={{ width: 160 }}
              />
              <FormControl size="small" sx={{ width: 130 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={row.currency}
                  label="Currency"
                  onChange={(e) => updateRow(idx, { currency: e.target.value })}
                >
                  {RRP_CURRENCY_CHOICES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <Tooltip title={`Remove ${row.geography} RRP`}>
                <IconButton size="small" onClick={() => removeRow(idx)}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          ))}

          {addableGeos.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {addableGeos.map((g) => (
                <Chip
                  key={g}
                  icon={<AddIcon />}
                  label={`${geoFlag(g)} ${g}`}
                  variant="outlined"
                  onClick={() => addGeo(g)}
                  clickable
                />
              ))}
            </Box>
          )}

          {/* Live Shopify storefront prices — reference for setting RRP.
              Max across currently-live listings with this exact SKU, with a
              per-store breakdown so the max is traceable (Rishabh 2026-07-06:
              a mislabeled MY store's RM119 read as "US $119"). */}
          {shopifyPrices.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Live on Shopify right now
              </Typography>
              <Table size="small" sx={{ mt: 0.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Geography</TableCell>
                    <TableCell align="right">Live price (max)</TableCell>
                    <TableCell align="right">Compare-at (max)</TableCell>
                    <TableCell align="right">Listings</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shopifyPrices.map((s) => (
                    <TableRow key={s.geography} hover>
                      <TableCell>{geoFlag(s.geography)} {s.geography}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatMoney(s.price_max, s.currency)}
                        </Typography>
                        {/* Per-store breakdown — which storefront charges what,
                            so the headline max is never a mystery number. */}
                        {(s.stores || []).length > 1 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(s.stores || []).slice(0, 4).map((st) => `${st.store} ${formatMoney(st.price, s.currency)}`).join(' · ')}
                            {(s.stores || []).length > 4 ? ` · +${s.stores.length - 4} more` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {s.compare_at_max > 0 ? formatMoney(s.compare_at_max, s.currency) : '—'}
                      </TableCell>
                      <TableCell align="right">{s.listings}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => adoptShopifyPrice(s.geography, s.price_max, s.currency)}>
                          Use as RRP
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* --- Price tiers containing this SKU --- */}
      <Card variant="outlined">
        <CardContent sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Price tiers <Typography component="span" variant="caption" color="text.secondary">
                  ({tiers.length} tiers include this SKU{Number(detail?.tiers_hidden_inactive) > 0 ? ` · ${detail.tiers_hidden_inactive} hidden — inactive customers` : ''})
                </Typography>
              </Typography>
            </Box>
            <Link href="/inventory/price-tiers" underline="hover" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              Manage tiers <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </Box>
        </CardContent>
        {tiers.length === 0 ? (
          <EmptyState title="This SKU is not in any price tier" hint="Add it from the Price Tiers admin." />
        ) : (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell align="right">This SKU's price</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Margin</TableCell>
                <TableCell align="right">RRP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tierGroups.map(([geo, groupTiers]) => (
                <React.Fragment key={geo}>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={5} sx={{ py: 0.75 }}>
                      <Typography variant="subtitle2" component="span">{geoFlag(geo)} {geoLabel(geo)}</Typography>
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        {groupTiers.length} tier{groupTiers.length > 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {groupTiers.map((t) => (
                    <TableRow key={t.tier_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{t.tier_name}</Typography>
                        {t.tier_code ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{t.tier_code}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{formatMoney(t.unit_price, t.currency)}</Typography>
                      </TableCell>
                      {/* Cost — the margin's own-warehouse basis, FX-converted
                          to the tier currency (Rishabh 2026-07-03). */}
                      <TableCell align="right">
                        {t.margin ? (
                          <Tooltip title={
                            `avg own-warehouse cost ${t.margin.basis_scope === 'geo' ? `in ${t.margin.basis_geo}` : '(all geographies)'}` +
                            `${t.margin.fx_rate !== 1 ? ` · ${formatMoney(t.margin.cogs, t.margin.cogs_currency)} @ ${t.margin.fx_rate}` : ''}`
                          }>
                            <Typography variant="body2" component="span" sx={{ cursor: 'help' }}>
                              {formatMoney(t.margin.cogs_converted, t.currency)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled" component="span">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {t.margin ? (
                          <Tooltip title={
                            `vs avg own-warehouse cost ${t.margin.basis_scope === 'geo' ? `in ${t.margin.basis_geo}` : '(all geographies — no local cost)'}: ` +
                            `${formatMoney(t.margin.cogs, t.margin.cogs_currency)} → ${formatMoney(t.margin.cogs_converted, t.currency)}` +
                            `${t.margin.fx_rate !== 1 ? ` @ ${t.margin.fx_rate}` : ''} · ${t.margin.basis_warehouses} warehouse${t.margin.basis_warehouses > 1 ? 's' : ''}`
                          }>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              component="span"
                              color={t.margin.pct < 0 ? 'error.main' : t.margin.pct < 25 ? 'warning.main' : 'success.main'}
                              sx={{ cursor: 'help' }}
                            >
                              {t.margin.pct}%
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled" component="span">—</Typography>
                        )}
                      </TableCell>
                      {/* RRP for the tier's geography (ASIA falls back to the
                          SG RRP row — same mapping the margin basis uses).
                          No saved RRP → fall back to the live storefront
                          reference (compare-at beats price: that's Shopify's
                          RRP slot), shown dimmed so saved vs live is visible
                          at a glance (Rishabh 2026-07-06). */}
                      <TableCell align="right">
                        {(() => {
                          const geoKey = t.geography === 'ASIA' ? 'SG' : t.geography;
                          const row = (detail?.rrp || []).find((r) => r.geography === geoKey);
                          if (row) {
                            return <Typography variant="body2" component="span">{formatMoney(row.rrp, row.currency)}</Typography>;
                          }
                          const live = shopifyPrices.find((s) => s.geography === geoKey);
                          const ref = live ? (live.compare_at_max > 0 ? live.compare_at_max : live.price_max) : null;
                          return ref ? (
                            <Tooltip title={`No saved ${geoKey} RRP — live storefront reference (highest ${live.compare_at_max > 0 ? 'compare-at' : 'price'} on your ${geoKey} stores). Save an RRP in the section above to pin it.`}>
                              <Typography variant="body2" component="span" color="text.secondary" sx={{ fontStyle: 'italic', cursor: 'help', textDecoration: 'underline dotted' }}>
                                {formatMoney(ref, live.currency)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled" component="span">—</Typography>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
}

/* =============================== BOM TAB =============================== */

export function ProductBomTab({ detail, loading, onEditProduction, onEditKit }) {
  const boms = useMemo(() => detail?.boms || [], [detail]);
  const [selected, setSelected] = useState(0);
  const hasProduction = useMemo(() => boms.some((b) => b.bom_type === 'production'), [boms]);

  // Group BOMs whose component recipe is IDENTICAL (same skus + quantities),
  // merging their geography pills — "if the BOM is identical across all
  // geographies, present it without duplication" (Rishabh).
  const groups = useMemo(() => {
    const signature = (bom) => bom.components
      .map((c) => `${String(c.component_sku).toLowerCase()}×${c.quantity}`)
      .sort()
      .join('|');
    const bySig = new Map();
    for (const bom of boms) {
      const sig = `${bom.bom_type}::${signature(bom)}`;
      if (!bySig.has(sig)) bySig.set(sig, { boms: [], geographies: new Set() });
      const g = bySig.get(sig);
      g.boms.push(bom);
      bom.geographies.forEach((geo) => g.geographies.add(geo));
    }
    return Array.from(bySig.values())
      .map((g) => ({
        bom: g.boms[0],
        alternatives: g.boms.map((b) => b.alternative_number),
        geographies: Array.from(g.geographies).sort(geoSort)
      }))
      .sort((a, b) => geoSort(a.geographies[0], b.geographies[0]));
  }, [boms]);

  if (loading) return <LoadingState />;
  if (!boms.length) {
    return (
      <Box>
        <EmptyState
          title="No BOM defined for this SKU"
          hint="Create a production BOM here for contract manufacturing, or manage sales kits via the Kit/Bundle section on Details."
        />
        {onEditProduction && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Button variant="contained" size="small" onClick={() => onEditProduction(null)}>
              Add production BOM
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  const singleUniversal = groups.length === 1;
  const active = groups[Math.min(selected, groups.length - 1)];
  const bom = active.bom;
  const totalLabel = formatMoneyRange(bom.total_cost_min, bom.total_cost_max, bom.cost_currency);

  return (
    <Box>
      {singleUniversal ? (
        <Alert severity="success" icon={false} sx={{ mb: 2 }}>
          <b>{active.geographies.includes('ALL') ? 'Same recipe in all geographies.' : 'One recipe covers every mapped geography.'}</b>{' '}
          {active.geographies.filter((g) => g !== 'ALL').map((g) => `${geoFlag(g)} ${g}`).join(' · ') || '🌍 ALL'}
        </Alert>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            This product is built differently per geography — {groups.length} recipes:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={Math.min(selected, groups.length - 1)}
            onChange={(e, v) => { if (v !== null) setSelected(v); }}
          >
            {groups.map((g, i) => (
              <ToggleButton key={i} value={i} sx={{ px: 1.5, textTransform: 'none' }}>
                {g.geographies.map((geo) => `${geoFlag(geo)} ${geo}`).join(' + ')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Card variant="outlined">
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {bom.bom_name || `Recipe${active.alternatives.length > 1 || active.alternatives[0] > 1 ? ` (alt ${active.alternatives.join(', ')})` : ''}`}
            </Typography>
            <Chip
              size="small"
              color={bom.bom_type === 'production' ? 'secondary' : 'primary'}
              variant="outlined"
              label={bom.bom_type === 'production' ? 'Production BOM' : 'Sales kit'}
            />
            {bom.is_master_default ? <Chip size="small" variant="outlined" label="Master default" /> : null}
            {bom.unpriced_count > 0 && (
              <Chip
                size="small"
                color="warning"
                icon={<WarningAmberIcon />}
                label={`${bom.unpriced_count} component${bom.unpriced_count > 1 ? 's' : ''} unpriced`}
              />
            )}
            {bom.fx_pending_count > 0 && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`${bom.fx_pending_count} awaiting exchange rate`}
              />
            )}
            {bom.estimated_count > 0 && (
              <Tooltip title="These component costs are migrated estimates, not actual purchase costs — the BOM total is only partly based on actual purchases. Enter/receive a real PO for these components to replace the estimate.">
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ color: 'text.secondary', borderColor: 'text.disabled', cursor: 'help' }}
                  label={`${bom.estimated_count}${bom.priced_count > 0 ? `/${bom.priced_count}` : ''} estimated (not actual purchases)`}
                />
              </Tooltip>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {bom.bom_type === 'production' && onEditProduction && (
              <Button size="small" variant="outlined" onClick={() => onEditProduction(bom)}>
                Edit recipe
              </Button>
            )}
            {bom.bom_type !== 'production' && onEditKit && (
              <Button size="small" variant="outlined" onClick={() => onEditKit()}>
                Edit kit
              </Button>
            )}
          </Box>
        </CardContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit cost</TableCell>
              <TableCell align="right">Line cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bom.components.map((c, i) => (
              <TableRow key={`${c.component_sku}-${i}`} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>{c.component_sku}</Typography>
                  {c.component_name ? (
                    <Typography variant="caption" color="text.secondary">{c.component_name}</Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{c.quantity}{c.unit ? ` ${c.unit}` : ''}</Typography>
                </TableCell>
                <TableCell align="right">
                  {c.cost_min === null ? (
                    (c.missing_rate_currencies || []).length ? (
                      <Tooltip title={`Cost exists in ${c.missing_rate_currencies.join(', ')} but there's no exchange rate yet to convert it to ${c.currency || bom.cost_currency || 'USD'}`}>
                        <Typography variant="body2" color="info.main" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>Awaiting rate</Typography>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No active cost row for this component">
                        <Typography variant="body2" color="warning.main">unpriced</Typography>
                      </Tooltip>
                    )
                  ) : (
                    // Converted components show their SOURCE cost inline under
                    // the converted value — "the cost these components came at"
                    // (Rishabh 2026-07-06) — plus the rate tooltip.
                    <Tooltip title={(c.cost_sources || []).some(s => s.rate !== 1)
                      ? (c.cost_sources || []).map(s =>
                          `${s.src_min === s.src_max ? s.src_min : `${s.src_min}–${s.src_max}`} ${s.currency}` +
                          `${s.rate !== 1 ? ` @ ${s.rate}` : ''} (${s.n} warehouse${s.n > 1 ? 's' : ''})`
                        ).join(' · ')
                      : ''}>
                      <Box component="span" sx={{ display: 'inline-block' }}>
                        <Typography variant="body2" sx={(c.cost_sources || []).some(s => s.rate !== 1) ? { cursor: 'help', textDecoration: 'underline dotted' } : undefined}>
                          {formatMoneyRange(c.cost_min, c.cost_max, c.currency)}
                        </Typography>
                        {(c.cost_sources || []).filter(s => s.rate !== 1).map(s => (
                          <Typography key={s.currency} variant="caption" color="text.secondary" display="block">
                            {formatMoneyRange(s.src_min, s.src_max, s.currency)} @ {s.rate}
                          </Typography>
                        ))}
                        {resolveCostTag(c.cost_source, c.component_name) && (
                          <Typography variant="caption" display="block" title={resolveCostTag(c.cost_source, c.component_name).title}
                            sx={{ color: resolveCostTag(c.cost_source, c.component_name).color, lineHeight: 1.2, cursor: 'help' }}>
                            {resolveCostTag(c.cost_source, c.component_name).label}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {c.cost_min === null ? '—' : (
                    <Typography variant="body2" fontWeight={600}>
                      {formatMoneyRange(c.quantity * c.cost_min, c.quantity * c.cost_max, c.currency)}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell colSpan={3}>
                <Typography variant="subtitle2">
                  Total BOM cost{bom.total_cost_min !== bom.total_cost_max ? ' (varies by warehouse)' : ''}
                  {bom.unpriced_count > 0 ? ' — excludes unpriced components' : ''}
                </Typography>
                {(bom.cost_converted || (bom.missing_rate_currencies || []).length > 0) && (
                  <Typography variant="caption" color={((bom.missing_rate_currencies || []).length > 0) ? 'warning.main' : 'text.secondary'}>
                    {bom.cost_converted ? `All costs converted to ${bom.cost_currency}${bom.fx_month ? ` at ${String(bom.fx_month).slice(0, 7)} exchange rates` : ''}` : ''}
                    {(bom.missing_rate_currencies || []).length > 0
                      ? `${bom.cost_converted ? ' · ' : ''}⚠ some costs in ${bom.missing_rate_currencies.join(', ')} have no exchange rate yet and are excluded`
                      : ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight={700}>{totalLabel}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      {onEditProduction && !hasProduction && (
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={() => onEditProduction(null)}>
            Add production BOM
          </Button>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Each component shows one unit cost — the cost to buy or make it at the factory. When more
        than one cost exists, an actual cost (from a purchase or a manually entered value) is used
        ahead of an estimate, and the most recent actual cost wins. "Awaiting rate" means a cost
        exists in another currency but there's no exchange rate yet to convert it.
        Use "Edit recipe" for production BOMs and "Edit kit" for sales kits.
      </Typography>
    </Box>
  );
}
